import { memo, useMemo } from "react";
import { CodeBlock } from "../codeBlock";
import { RenderTable } from "../renderTable";
import { preprocessLaTeX } from "utils/functions";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import styles from "./markdown.module.scss";
import classNames from "classnames";
import { Tooltip } from "components";
import { WordHighlighter } from "views/layout/Sidebar/component/ChatSidebar/components/ChatItem/WordHighlighter";

interface IProps {
  message: string;
  searchQuery?: string;
  highlighted?: boolean;
  isHighlighted?: boolean;
  threads?: boolean;
  isStreaming?: boolean;
}

export const processMessageContent = (text: string): string => {
    if (!text || typeof text !== 'string') return '';

  let processed = text;

  // Step 1: Decode unicode
  processed = processed.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );

  // Step 2: Replace escaped quotes and backslashes first
  processed = processed.replace(/\\"/g, '"');
  processed = processed.replace(/\\\\/g, '\\');

  // Step 3: Normalize newlines
  processed = processed.replace(/\\n\\n/g, '\n\n').replace(/\\n/g, '\n');
  processed = processed.replace(/""\s/g, ' ');
  processed = processed.replace(/\s""/g, ' ');
  processed = processed.replace(/""/g, ''); 


  // Step 4: Recursively trim outer quotes
  let prev;
  do {
    prev = processed;
    processed = processed.trim();
    if (
      (processed.startsWith('"') && processed.endsWith('"')) ||
      (processed.startsWith("'") && processed.endsWith("'"))
    ) {
      processed = processed.slice(1, -1);
    }
  } while (prev !== processed);

  return processed;
};

export const MarkdownComponent: React.FC<IProps> = memo(
  ({ searchQuery, message, highlighted, isHighlighted, threads }) => {
    const processedMessage = useMemo(() => {
      const cleanedText = processMessageContent(message);
      return preprocessLaTeX(cleanedText);
    }, [message]);

    const components: Components = {
      code({ node, className, children, ...props }) {
        if (!node?.position || !children) {
          return <>{children}</>;
        }
        if (node?.position.start.line === node?.position.end.line) {
          return (
            <span className={`font-semibold px-1 py-[2px] mx-1 bg-[#e8e8eb] dark:bg-[#2f2d34] rounded-md text-[14px]`}>
              <code className={className} {...props}>
                {children}
              </code>
            </span>
          );
        }

        const match = /language-(\w+)/.exec(className || "");
        let language = match ? match[1].toLowerCase() : "javascript";
        return (
          <CodeBlock
            content={String(children).replace(/\n$/, "")}
            language={language}
            searchWord={searchQuery}
            isHighlighted={isHighlighted}
            highlighted={highlighted}
            threads={threads}
          />
        );
      },
      a({ href, children }) {
        const text = children ? children.toString() : "";
        if (text.match(/^\[\d+\]$/)) {
          return (
            <Tooltip
            tooltipAdjustement={true}
            regenrate={true}
            control={
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
            >
              {children}
            </a>
            }
            placement="top"
            theme="light"
          >
            <div>
              {/* Content for the tooltip */}
              {`${href}`}
            </div>
          </Tooltip>
          );
        }
        else if (text.includes("http")) {
          return <span>{text}</span>;
        } else {
          return (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="text-primary underline"
            >
              {children}
            </a>
          );
        }
      },
      strong({ children }) {
        return <strong>{children}</strong>;
      },

      table({ children }) {
        return <RenderTable>{children}</RenderTable>;
      },

      text({ children }) {
        if (!searchQuery) {
          return <>{children}</>;
        }
        return (
          <WordHighlighter
            searchQuery={searchQuery}
            text={children as string}
            excerptLength={0}
          />
        );
      },
    };

    return (
      <div
        className={classNames(styles.markdownContainer, {
          "font-inter text-[14px] text-[#171717] dark:text-[rgba(255,255,255,0.9)]":
            threads,
        })}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[rehypeKatex as any]}
          components={components}
          // children={preprocessLaTeX(fixedMessage)}
        >
          {processedMessage}
        </ReactMarkdown>
      </div>
    );
  }
);
